import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import USPhoneInput from "../components/USPhoneInput";
import Footer from "../components/Footer";

const inputClass =
  "w-full font-constantia text-black-deep bg-white-pure border border-gray-200 rounded-lg px-4 py-2.5 text-sm leading-relaxed outline-none transition-all duration-200 placeholder-gray-400 focus:border-blue-primary focus:shadow-[0_0_0_3px_rgba(0,45,181,0.08)]";
const labelClass = "block font-constantia text-black-deep mb-1.5";
const labelStyle = { fontSize: "14px", opacity: 0.8 };
const req = <span className="text-red-500 ml-0.5">*</span>;

const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM",  "3:00 PM",  "4:00 PM",
];

// Minimum bookable date = tomorrow
function minDate() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
}

function isWeekend(dateStr) {
  if (!dateStr) return false;
  const day = new Date(dateStr + "T12:00:00").getDay();
  return day === 0 || day === 6;
}

// ─── Step indicator ────────────────────────────────────────────────────────────
function StepBar({ step }) {
  const labels = ["Your Details", "Schedule", "Review"];
  return (
    <div className="flex items-center justify-center mb-8">
      {labels.map((label, i) => {
        const n      = i + 1;
        const done   = step > n;
        const active = step === n;
        return (
          <div key={label} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center font-cinzel font-bold transition-all duration-300"
                style={{
                  fontSize: "12px",
                  backgroundColor: done ? "#012E72" : active ? "#002DB5" : "rgba(1,46,114,0.1)",
                  color: done || active ? "#FFFFFF" : "rgba(1,46,114,0.4)",
                }}
              >
                {done ? (
                  <svg width="12" height="12" viewBox="0 0 10 10" fill="none">
                    <polyline points="1.5 5 4 7.5 8.5 2.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : n}
              </div>
              <span
                className="font-cinzel uppercase hidden sm:block"
                style={{ fontSize: "9px", letterSpacing: "0.12em", color: active ? "#012E72" : "rgba(1,46,114,0.35)", whiteSpace: "nowrap" }}
              >
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div
                className="w-14 sm:w-20 h-px mx-2 mb-5 transition-colors duration-300"
                style={{ backgroundColor: step > n ? "#012E72" : "rgba(1,46,114,0.15)" }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Time slot picker ─────────────────────────────────────────────────────────
function TimeSlotPicker({ selected, onSelect }) {
  return (
    <div className="grid grid-cols-4 gap-2">
      {TIME_SLOTS.map((slot) => {
        const active = selected === slot;
        return (
          <button
            key={slot}
            type="button"
            onClick={() => onSelect(slot)}
            className="py-2.5 rounded-lg font-cinzel uppercase transition-all duration-200 hover:-translate-y-0.5"
            style={{
              fontSize: "10px",
              letterSpacing: "0.08em",
              backgroundColor: active ? "#012E72" : "rgba(1,46,114,0.05)",
              color: active ? "#FFFFFF" : "#012E72",
              border: active ? "1.5px solid #012E72" : "1.5px solid rgba(1,46,114,0.15)",
              boxShadow: active ? "0 4px 14px rgba(1,46,114,0.25)" : "none",
            }}
          >
            {slot}
          </button>
        );
      })}
    </div>
  );
}

// ─── Review row ───────────────────────────────────────────────────────────────
function ReviewRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-4 px-5 py-3 border-b last:border-0" style={{ borderColor: "rgba(1,46,114,0.07)" }}>
      <span className="font-cinzel uppercase flex-shrink-0 w-28 pt-0.5" style={{ fontSize: "9px", letterSpacing: "0.15em", color: "rgba(1,46,114,0.5)" }}>
        {label}
      </span>
      <span className="font-constantia text-black-deep" style={{ fontSize: "14px", opacity: 0.85 }}>{value}</span>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ConsultationPage() {
  usePageTitle("Request a Consultation");
  const navigate = useNavigate();

  const [step, setStep]             = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);
  const [dateWarn, setDateWarn]     = useState("");

  const [form, setForm] = useState({
    fullName: "", email: "", address: "", phone: "", details: "",
    preferredDate: "", preferredTime: "",
    altDate: "", altTime: "",
    contactMethod: "phone",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "preferredDate" && isWeekend(value)) {
      setDateWarn("We're open Monday – Friday only. Please select a weekday.");
    } else if (field === "preferredDate") {
      setDateWarn("");
    }
  };

  const step1Valid = form.fullName && form.email && form.address && form.phone;
  const step2Valid = form.preferredDate && !isWeekend(form.preferredDate) && form.preferredTime && form.contactMethod;

  const goToStep = (n) => { setError(null); setStep(n); window.scrollTo(0, 0); };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/consultations`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Submission failed.");
      setStep(4);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "";
    const dt = new Date(d + "T12:00:00");
    return dt.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };

  return (
    <>
      <main className="min-h-screen w-full">

        {/* ── Hero ── */}
        <section className="relative bg-blue-dark overflow-hidden py-20 sm:py-28">
          <div className="absolute inset-0 pointer-events-none" style={{
            backgroundImage: `radial-gradient(ellipse 80% 70% at 50% 0%, rgba(0,45,181,0.4) 0%, transparent 70%),
                              radial-gradient(ellipse 50% 45% at 100% 100%, rgba(0,20,90,0.35) 0%, transparent 60%)`,
          }} />
          <div className="absolute inset-0 pointer-events-none" style={{
            backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)",
            backgroundSize: "80px 80px",
          }} />
          <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            backgroundSize: "200px 200px",
          }} />

          <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <div className="inline-flex items-center gap-2 mb-7">
              <div className="w-6 h-px bg-white-pure/30" />
              <span className="font-cinzel font-bold text-[0.58rem] tracking-[0.3em] uppercase text-white-pure/55 border border-white-pure/18 px-4 py-[5px] rounded-full">
                Free · No Obligation
              </span>
              <div className="w-6 h-px bg-white-pure/30" />
            </div>

            <h1
              className="font-cinzel font-black text-white-pure tracking-[0.04em] leading-[1.1] mb-5"
              style={{ fontSize: "clamp(1.55rem, 4.5vw, 3rem)", textShadow: "0 2px 48px rgba(0,45,181,0.55)" }}
            >
              REQUEST A PERSONALIZED<br className="hidden sm:block" /> CONSULTATION
            </h1>

            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="w-12 h-px bg-gradient-to-r from-transparent to-white-pure/30" />
              <div className="w-1.5 h-1.5 rounded-full bg-white-pure/40 border border-white-pure/55" />
              <div className="w-12 h-px bg-gradient-to-l from-transparent to-white-pure/30" />
            </div>

            <p className="font-constantia italic text-white-pure/55 leading-[1.85] max-w-xl mx-auto" style={{ fontSize: "clamp(0.88rem, 1.6vw, 1.05rem)" }}>
              Pick a date and time that works for you. A licensed agent will confirm and follow up at your chosen slot — at no cost to you.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
              {["No obligation", "Licensed agents", "Phone or video", "Mon – Fri 9 AM – 5 PM"].map((b) => (
                <span key={b} className="font-cinzel text-[0.6rem] tracking-[0.15em] uppercase text-white-pure/50 border border-white-pure/15 px-4 py-[5px] rounded-full">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── Form section ── */}
        <section className="py-16 sm:py-20 lg:py-24" style={{ background: "linear-gradient(160deg,#EEF2F8 0%,#E8EDF6 50%,#EDF1F9 100%)" }}>
          <div className="max-w-2xl mx-auto px-4 sm:px-6">

            {/* ── Success ── */}
            {step === 4 ? (
              <div className="bg-white-pure rounded-2xl border border-gray-200 p-10 text-center shadow-sm">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: "rgba(1,46,114,0.08)" }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2 className="font-cinzel font-bold text-blue-dark uppercase mb-3" style={{ fontSize: "20px", letterSpacing: "0.08em" }}>
                  You're Scheduled!
                </h2>
                <p className="font-constantia text-black-deep leading-relaxed mb-2 max-w-md mx-auto" style={{ fontSize: "15px", opacity: 0.65 }}>
                  Thank you, <strong>{form.fullName}</strong>. We've noted your preferred slot of{" "}
                  <strong>{form.preferredTime}</strong> on <strong>{formatDate(form.preferredDate)}</strong>.
                  A licensed agent will confirm and reach out shortly.
                </p>
                <p className="font-constantia italic mb-8" style={{ fontSize: "13px", color: "rgba(1,7,4,0.4)" }}>
                  Confirmation sent to <strong>{form.email}</strong>
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    onClick={() => navigate("/")}
                    className="font-cinzel uppercase tracking-widest bg-blue-dark text-white-pure px-8 py-3 rounded-full hover:bg-blue-primary transition-colors duration-200"
                    style={{ fontSize: "11px" }}
                  >
                    Back to Home
                  </button>
                  <button
                    onClick={() => navigate("/quote")}
                    className="font-cinzel uppercase tracking-widest text-blue-dark border border-blue-dark/30 px-8 py-3 rounded-full hover:bg-blue-dark hover:text-white-pure transition-colors duration-200"
                    style={{ fontSize: "11px" }}
                  >
                    Request a Full Quote
                  </button>
                </div>
              </div>
            ) : (
              <>
                <StepBar step={step} />

                <div className="bg-white-pure rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

                  {/* Card header */}
                  <div className="px-8 py-5 border-b border-gray-100">
                    <h2 className="font-cinzel font-bold text-blue-dark" style={{ fontSize: "16px", letterSpacing: "0.05em" }}>
                      {step === 1 && "Your Contact Details"}
                      {step === 2 && "Pick a Time"}
                      {step === 3 && "Review Your Request"}
                    </h2>
                    <p className="font-constantia mt-1" style={{ fontSize: "13px", color: "rgba(1,7,4,0.45)" }}>
                      {step === 1 && "Fill in your details so we know how to reach you."}
                      {step === 2 && "Select your preferred date and time. Our office is open Monday – Friday, 9 AM – 5 PM."}
                      {step === 3 && "Confirm everything looks right before we lock it in."}
                    </p>
                  </div>

                  <div className="px-8 py-6">

                    {/* ── Step 1: Contact details ── */}
                    {step === 1 && (
                      <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className={labelClass} style={labelStyle}>Full name {req}</label>
                            <input type="text" name="fullName" placeholder="John Doe" value={form.fullName} onChange={handleChange} className={inputClass} />
                          </div>
                          <div>
                            <label className={labelClass} style={labelStyle}>Email address {req}</label>
                            <input type="email" name="email" placeholder="john@example.com" value={form.email} onChange={handleChange} className={inputClass} />
                          </div>
                          <div>
                            <label className={labelClass} style={labelStyle}>Mailing address {req}</label>
                            <input type="text" name="address" placeholder="Street address, city, state" value={form.address} onChange={handleChange} className={inputClass} />
                          </div>
                          <USPhoneInput name="phone" value={form.phone} onChange={handleChange} label="Phone number" required />
                        </div>
                        <div>
                          <label className={labelClass} style={labelStyle}>
                            What would you like to discuss?{" "}
                            <span className="font-constantia" style={{ fontSize: "12px", opacity: 0.5 }}>(optional)</span>
                          </label>
                          <textarea
                            name="details" rows={3}
                            placeholder="Coverage needs, questions, or any context that helps us prepare..."
                            value={form.details} onChange={handleChange}
                            className={`${inputClass} resize-none`}
                          />
                        </div>
                      </div>
                    )}

                    {/* ── Step 2: Schedule ── */}
                    {step === 2 && (
                      <div className="flex flex-col gap-6">

                        {/* Preferred date */}
                        <div>
                          <label className={labelClass} style={labelStyle}>Preferred date {req}</label>
                          <input
                            type="date"
                            min={minDate()}
                            value={form.preferredDate}
                            onChange={(e) => handleDateChange("preferredDate", e.target.value)}
                            className={inputClass}
                            style={{ colorScheme: "light" }}
                          />
                          {dateWarn && (
                            <p className="font-constantia text-amber-600 text-xs mt-1.5 flex items-center gap-1">
                              <span>⚠</span> {dateWarn}
                            </p>
                          )}
                        </div>

                        {/* Preferred time */}
                        <div>
                          <label className={labelClass} style={labelStyle}>Preferred time {req}</label>
                          <TimeSlotPicker
                            selected={form.preferredTime}
                            onSelect={(slot) => setForm((p) => ({ ...p, preferredTime: slot }))}
                          />
                        </div>

                        {/* Contact method */}
                        <div>
                          <label className={labelClass} style={labelStyle}>How would you like to meet? {req}</label>
                          <div className="flex gap-3">
                            {[
                              { value: "phone", label: "Phone Call",  icon: "📞" },
                              { value: "video", label: "Video Call",  icon: "💻" },
                            ].map(({ value, label, icon }) => {
                              const active = form.contactMethod === value;
                              return (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() => setForm((p) => ({ ...p, contactMethod: value }))}
                                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-cinzel uppercase transition-all duration-200"
                                  style={{
                                    fontSize: "11px",
                                    letterSpacing: "0.1em",
                                    backgroundColor: active ? "#012E72" : "rgba(1,46,114,0.04)",
                                    color: active ? "#FFFFFF" : "#012E72",
                                    border: active ? "1.5px solid #012E72" : "1.5px solid rgba(1,46,114,0.15)",
                                    boxShadow: active ? "0 4px 14px rgba(1,46,114,0.2)" : "none",
                                  }}
                                >
                                  <span style={{ fontSize: "16px" }}>{icon}</span>
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Alternate slot (optional) */}
                        <div className="rounded-xl p-5" style={{ backgroundColor: "rgba(1,46,114,0.03)", border: "1px solid rgba(1,46,114,0.1)" }}>
                          <p className="font-cinzel uppercase tracking-widest mb-4" style={{ fontSize: "10px", color: "rgba(1,46,114,0.5)" }}>
                            Alternate Time Slot <span className="normal-case font-constantia not-italic" style={{ letterSpacing: 0, textTransform: "none", opacity: 0.6 }}>(optional — in case your first choice isn't available)</span>
                          </p>
                          <div className="flex flex-col gap-4">
                            <div>
                              <label className={labelClass} style={labelStyle}>Alternate date</label>
                              <input
                                type="date"
                                min={minDate()}
                                value={form.altDate}
                                onChange={(e) => handleDateChange("altDate", e.target.value)}
                                className={inputClass}
                                style={{ colorScheme: "light" }}
                              />
                            </div>
                            {form.altDate && (
                              <div>
                                <label className={labelClass} style={labelStyle}>Alternate time</label>
                                <TimeSlotPicker
                                  selected={form.altTime}
                                  onSelect={(slot) => setForm((p) => ({ ...p, altTime: slot }))}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                      </div>
                    )}

                    {/* ── Step 3: Review ── */}
                    {step === 3 && (
                      <div className="flex flex-col gap-4">
                        <div>
                          <p className="font-cinzel uppercase tracking-widest mb-2" style={{ fontSize: "10px", color: "rgba(1,46,114,0.45)" }}>Contact Details</p>
                          <div className="rounded-xl border border-gray-200 overflow-hidden">
                            <ReviewRow label="Full Name" value={form.fullName} />
                            <ReviewRow label="Email"     value={form.email} />
                            <ReviewRow label="Address"   value={form.address} />
                            <ReviewRow label="Phone"     value={form.phone} />
                            <ReviewRow label="Details"   value={form.details || "—"} />
                          </div>
                        </div>
                        <div>
                          <p className="font-cinzel uppercase tracking-widest mb-2" style={{ fontSize: "10px", color: "rgba(1,46,114,0.45)" }}>Appointment</p>
                          <div className="rounded-xl border border-gray-200 overflow-hidden">
                            <ReviewRow label="Preferred"  value={`${formatDate(form.preferredDate)} at ${form.preferredTime}`} />
                            <ReviewRow label="Alternate"  value={form.altDate ? `${formatDate(form.altDate)}${form.altTime ? ` at ${form.altTime}` : ""}` : "—"} />
                            <ReviewRow label="Meeting"    value={form.contactMethod === "video" ? "Video Call" : "Phone Call"} />
                          </div>
                        </div>
                      </div>
                    )}

                    {error && (
                      <div className="mt-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200">
                        <p className="font-constantia text-red-700 text-sm">{error}</p>
                      </div>
                    )}

                    {/* Nav buttons */}
                    <div className="flex gap-3 mt-6">
                      {step > 1 && (
                        <button
                          onClick={() => goToStep(step - 1)}
                          className="flex-1 font-cinzel uppercase tracking-widest text-blue-dark border border-blue-dark/30 py-3 rounded-xl hover:bg-blue-dark hover:text-white-pure transition-colors duration-200"
                          style={{ fontSize: "11px" }}
                        >
                          ← Back
                        </button>
                      )}

                      {step < 3 ? (
                        <button
                          onClick={() => goToStep(step + 1)}
                          disabled={step === 1 ? !step1Valid : !step2Valid}
                          className="flex-[3] bg-blue-dark text-white-pure font-cinzel uppercase tracking-widest py-3 rounded-xl hover:bg-blue-primary transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          style={{ fontSize: "12px", letterSpacing: "0.15em" }}
                        >
                          {step === 1 ? "Pick a Time →" : "Review →"}
                        </button>
                      ) : (
                        <button
                          onClick={handleSubmit}
                          disabled={submitting}
                          className="flex-[3] bg-blue-dark text-white-pure font-cinzel uppercase tracking-widest py-3 rounded-xl hover:bg-blue-primary transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          style={{ fontSize: "12px", letterSpacing: "0.15em" }}
                        >
                          {submitting ? "Submitting..." : "Confirm Booking"}
                          {!submitting && (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-center font-constantia mt-6" style={{ fontSize: "13px", color: "rgba(1,7,4,0.45)" }}>
                  Ready for a full application?{" "}
                  <button onClick={() => navigate("/quote")} className="text-blue-dark underline hover:text-blue-primary transition-colors">
                    Request a quote instead
                  </button>
                </p>
              </>
            )}
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
